import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Upload, FileSearch, ShieldCheck, ArrowLeft, QrCode } from "lucide-react";
import { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import LegalFooter from "@/components/LegalFooter";

const DOC_ICONS = {
  ndnca: "NDNCA",
  mandate_sale: "MND-V",
  mandate_search: "MND-R",
  mandate_agent: "MND-A",
  intermediation_agent: "INT",
  intermediation_bilateral: "INT+",
  direct_contract: "DIR",
};

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const locale = { it: "it-IT", en: "en-US", he: "he-IL", ru: "ru-RU" }[lang] || "it-IT";
    return d.toLocaleString(locale, { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function Verify() {
  const { lang } = useApp();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hashInput, setHashInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [fromQR, setFromQR] = useState(false);
  const fileRef = useRef(null);
  const [searchParams] = useSearchParams();

  const verifyRef = useCallback(async (ref) => {
    setBusy(true);
    setResult(null);
    setFromQR(true);
    try {
      const { data } = await axios.get(`${API}/public/verify-by-ref/${encodeURIComponent(ref)}`);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica");
    } finally {
      setBusy(false);
    }
  }, []);

  const verifyHashDirect = useCallback(async (h) => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/public/verify/${h}`);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const ref = searchParams.get("ref");
    const hash = searchParams.get("hash");
    if (ref) {
      verifyRef(ref);
    } else if (hash && hash.length === 64) {
      setHashInput(hash);
      verifyHashDirect(hash.toLowerCase());
    }
  }, [searchParams, verifyRef, verifyHashDirect]);

  const verifyFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error(lang === "it" ? "Carica un file PDF" : "Please upload a PDF");
      return;
    }
    setBusy(true);
    setResult(null);
    setFromQR(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/public/verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica");
    } finally {
      setBusy(false);
    }
  }, [lang]);

  const verifyHash = useCallback(async () => {
    const h = hashInput.trim().toLowerCase();
    if (h.length !== 64) {
      toast.error(lang === "it" ? "Hash SHA-256 deve essere di 64 caratteri" : "SHA-256 must be 64 chars");
      return;
    }
    setBusy(true);
    setResult(null);
    setFromQR(false);
    try {
      const { data } = await axios.get(`${API}/public/verify/${h}`);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica");
    } finally {
      setBusy(false);
    }
  }, [hashInput, lang]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) verifyFile(f);
  }, [verifyFile]);

  return (
    <div className="min-h-screen bg-[#F5F0E6] text-[#2E2A22]" data-testid="verify-page">
      {/* Header */}
      <header className="border-b border-[#D8CFBB] bg-[#FBF8F1]/80 backdrop-blur">
        <div className="mx-auto max-w-5xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="verify-home-link">
            <img src="/aaron-logo.png" alt="Aaron Properties" className="h-8 w-auto" />
            <span className="font-[Cormorant_Garamond,serif] text-2xl tracking-wide">Aaron Properties</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#7A6E5A] hover:text-[#2E2A22] transition-colors flex items-center gap-1"
            data-testid="verify-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> {t("verify_back", lang)}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#2E2A22] text-[#C5A880] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">Aaron Properties · Verifica</p>
        </div>
        <h1 className="font-[Cormorant_Garamond,serif] text-4xl sm:text-5xl leading-tight mb-4">
          {t("verify_title", lang)}
        </h1>
        <p className="text-[#5A5040] leading-relaxed max-w-2xl mb-10">
          {t("verify_sub", lang)}
        </p>

        {/* Upload area */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          data-testid="verify-dropzone"
          className={`cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition-all ${
            dragOver
              ? "border-[#C5A880] bg-[#F1E9D6]"
              : "border-[#D8CFBB] bg-[#FBF8F1] hover:border-[#C5A880] hover:bg-[#F5F0E6]"
          }`}
        >
          <Upload className="w-8 h-8 mx-auto mb-3 text-[#C5A880]" />
          <p className="text-sm text-[#5A5040]">{t("verify_upload", lang)}</p>
          <p className="text-xs text-[#7A6E5A] mt-1">PDF · max 20 MB</p>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="verify-file-input"
            onChange={(e) => verifyFile(e.target.files?.[0])}
          />
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-[#D8CFBB]" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#B8A98F]">{t("verify_or", lang)}</span>
          <div className="flex-1 h-px bg-[#D8CFBB]" />
        </div>

        {/* Hash input */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder={t("verify_paste_hash", lang)}
            value={hashInput}
            onChange={(e) => setHashInput(e.target.value)}
            className="flex-1 border border-[#D8CFBB] bg-white rounded-lg px-4 py-3 text-sm font-mono text-[#2E2A22] focus:outline-none focus:border-[#C5A880]"
            data-testid="verify-hash-input"
            maxLength={64}
          />
          <button
            onClick={verifyHash}
            disabled={busy}
            data-testid="verify-hash-submit"
            className="px-6 py-3 bg-[#2E2A22] text-[#F5F0E6] rounded-lg text-sm font-medium hover:bg-[#1F1D18] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <FileSearch className="w-4 h-4" /> {t("verify_check", lang)}
          </button>
        </div>

        {/* Busy state */}
        {busy && (
          <div className="mt-10 text-center text-sm text-[#7A6E5A]" data-testid="verify-loading">
            {t("loading", lang)}
          </div>
        )}

        {/* Result */}
        {!busy && result && (
          <>
            {fromQR && (
              <div className="mt-8 flex items-center gap-2 text-xs text-[#8a7658] bg-[#F1E9D6] border border-[#DDD5C4] px-4 py-2 rounded-lg" data-testid="verify-from-qr-banner">
                <QrCode className="w-4 h-4" />
                {lang === "it"
                  ? "Verifica avviata tramite QR code del documento"
                  : "Verification opened via document QR code"}
              </div>
            )}
            <VerifyResult result={result} lang={lang} />
          </>
        )}
      </main>

      <LegalFooter />
    </div>
  );
}

function VerifyResult({ result, lang }) {
  const ok = result.valid;
  return (
    <div
      className={`mt-10 rounded-xl border-2 overflow-hidden ${
        ok ? "border-emerald-600 bg-emerald-50/60" : "border-red-500 bg-red-50/60"
      }`}
      data-testid={ok ? "verify-result-valid" : "verify-result-invalid"}
    >
      <div className={`p-6 flex items-start gap-4 ${ok ? "bg-emerald-100/50" : "bg-red-100/50"}`}>
        {ok ? (
          <CheckCircle2 className="w-8 h-8 text-emerald-700 flex-shrink-0" />
        ) : (
          <XCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <h3 className={`text-xl font-[Cormorant_Garamond,serif] ${ok ? "text-emerald-900" : "text-red-900"}`}>
            {ok ? t("verify_valid", lang) : t("verify_invalid", lang)}
          </h3>
          <p className={`text-sm mt-1 ${ok ? "text-emerald-800" : "text-red-800"}`}>{result.message}</p>
        </div>
      </div>

      {ok && (
        <div className="p-6 bg-white/70 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <Field label={t("verify_doctype", lang)} value={result.document_label} testid="verify-field-doctype" mono={false} big />
          <Field label={t("verify_ref", lang)} value={result.reference_code} testid="verify-field-ref" mono />
          <Field label={t("verify_signer", lang)} value={result.signer_name} testid="verify-field-signer" />
          <Field label={t("verify_signed_at", lang)} value={formatDate(result.signed_at, lang)} testid="verify-field-signed-at" />
          {result.document_type?.startsWith("intermediation") && (
            <div className="sm:col-span-2 text-xs text-emerald-800 bg-emerald-100/60 rounded-lg px-3 py-2 border border-emerald-200">
              {result.bilateral ? "✓ " : "◇ "}
              {(() => {
                if (result.bilateral) return t("verify_bilateral", lang);
                return lang === "it"
                  ? "Solo firma agente (in attesa buyer)"
                  : "Agent-side only (pending buyer signature)";
              })()}
            </div>
          )}
          {result.sha256 && (
            <div className="sm:col-span-2">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#B8A98F] mb-1">{t("verify_hash", lang)}</p>
              <p className="font-mono text-xs text-[#2E2A22] break-all" data-testid="verify-field-hash">{result.sha256}</p>
            </div>
          )}
        </div>
      )}

      {!ok && (
        <div className="p-6 bg-white/70 text-sm text-[#5A5040]">
          {result.sha256 && (
            <>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#B8A98F] mb-1">{t("verify_hash", lang)}</p>
              <p className="font-mono text-xs text-[#7A6E5A] break-all" data-testid="verify-field-hash">{result.sha256}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, testid, mono = false, big = false }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#B8A98F] mb-1">{label}</p>
      <p
        className={`${mono ? "font-mono" : ""} ${big ? "text-base" : "text-sm"} text-[#2E2A22]`}
        data-testid={testid}
      >
        {value || "—"}
      </p>
    </div>
  );
}
